/**
 * ScheduleIntelWorkspace — the Phase 4 "Pulse" surface: where the team runs the nightly scheduler
 * on demand and (soon) reads what it changed, the schedule-health scores, and the chat.
 *
 * v1 ships only the RUN CONTROL + recent-run status (the in-app trigger that replaces clicking
 * around GitHub). The daily-change digest, composite scores, and the chat land here next, in the
 * placeholder slot below — this surface is their home.
 *
 * Workspace recipe mirrors BillingWorkspace: open/close via `open-pulse`/`close-pulse` events, active
 * state persisted to session/localStorage, `?ws=pulse` URL, docked tray-aware via `.aj-pulse-ws`.
 * Gated to (hasModule('workload') || hasModule('admin')) for now (broaden to a manager module later, no DB dependency to ship).
 */

import { useEffect, useRef, useState } from 'react'
import { hasModule, isSeatLoaded } from '../services/tenancy.js'
import { isEmbedded } from '../outline/state.js'
import { triggerSchedulerRun, getSchedulerRuns, getSchedulerRunHistory, askSchedule, triggerSnapshot } from '../services/scheduler.js'
import { getActiveProjects } from '../services/commands.js'
import { confirmDialog } from './Dialog.jsx'
import { WorkspaceHeader } from './WorkspaceChrome.jsx'

const STORE_KEY = 'aj:pulse:ws'

function readStored() {
  try {
    const ws = new URLSearchParams(window.location.search).get('ws')
    if (ws === 'pulse') return { active: true }
    if (ws !== null) return null   // a different workspace is explicit in the URL
    // A peek/split iframe (?split=1) must never auto-restore a workspace from storage — Matt:
    // "the modal opens in another workspace, like Pulse or CRM depending on the latest open
    // state." Same-origin storage is shared with the parent tab, so without this the iframe
    // would silently pick up whatever was left open there.
    if (isEmbedded()) return null
    const t = sessionStorage.getItem(STORE_KEY)
    if (t) return JSON.parse(t)
    return JSON.parse(localStorage.getItem(STORE_KEY) || 'null')
  } catch (e) { return null }
}

/** Searchable project picker for the run scope — type to filter active projects; blank = firm-wide. */
function ProjectPicker({ value, onChange }) {
  const [projects, setProjects] = useState(null)
  const [open, setOpen] = useState(false)
  const fetchedRef = useRef(false)
  function ensureLoaded() {
    if (fetchedRef.current) return
    fetchedRef.current = true
    getActiveProjects()
      .then(recs => setProjects((recs || []).map(r => ({
        name: String((r.fields || {})['Project Name'] || '').trim(),
        number: String((r.fields || {})['Project Number'] || '').trim(),
      })).filter(p => p.name)))
      .catch(() => setProjects([]))
  }
  const ql = (value || '').trim().toLowerCase()
  const all = projects || []
  const matches = !ql ? all : all.filter(p => {
    const hay = (p.number + ' ' + p.name).toLowerCase()
    return ql.split(/\s+/).every(t => hay.includes(t))
  })
  return (
    <div style={S.pickerWrap}>
      <input
        style={S.input2}
        placeholder="Search a project — blank = firm-wide"
        value={value}
        onFocus={() => { ensureLoaded(); setOpen(true) }}
        onChange={e => { onChange(e.target.value); setOpen(true) }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && (
        <div style={S.pickerMenu}>
          {!projects && <div style={S.pickerEmpty}>Loading projects…</div>}
          {projects && matches.length === 0 && <div style={S.pickerEmpty}>No match</div>}
          {matches.slice(0, 12).map(p => (
            <button key={p.name} style={S.pickerItem}
              onMouseDown={e => { e.preventDefault(); onChange(p.name); setOpen(false) }}>
              {p.number ? <span style={S.pickerNum}>{p.number}</span> : null}{p.name}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default function ScheduleIntelWorkspace() {
  const stored = readStored()
  const [active, setActive] = useState(!!(stored && stored.active))
  const [seatReady, setSeatReady] = useState(isSeatLoaded())

  const [mode, setMode] = useState('read_only')
  const [testProject, setTestProject] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState(null)        // { kind: 'ok'|'err', text }
  const [runs, setRuns] = useState(null)      // null = not loaded, [] = loaded empty
  const [loadingRuns, setLoadingRuns] = useState(false)
  const [digest, setDigest] = useState(null)  // schedule_runs history (what changed)
  const [loadingDigest, setLoadingDigest] = useState(false)
  const [chat, setChat] = useState([])        // [{ role, content }]
  const [chatInput, setChatInput] = useState('')
  const [chatBusy, setChatBusy] = useState(false)
  const [snapBusy, setSnapBusy] = useState(false)
  const [snapMsg, setSnapMsg] = useState(null)
  const pollTimers = useRef([])

  // open/close via events
  useEffect(() => {
    function onOpen() { setActive(true) }
    function onClose() { setActive(false) }
    window.addEventListener('open-pulse', onOpen)
    window.addEventListener('close-pulse', onClose)
    return () => { window.removeEventListener('open-pulse', onOpen); window.removeEventListener('close-pulse', onClose) }
  }, [])

  // persist active state (per-tab + shared snapshot)
  useEffect(() => {
    try {
      if (active) { const j = JSON.stringify({ active: true }); sessionStorage.setItem(STORE_KEY, j); localStorage.setItem(STORE_KEY, j) }
      else { sessionStorage.removeItem(STORE_KEY); localStorage.removeItem(STORE_KEY) }
    } catch (e) { /* ignore */ }
  }, [active])

  // URL + title
  useEffect(() => {
    const url = new URL(window.location)
    if (active) { url.searchParams.set('ws', 'pulse'); document.title = 'Alder James Schedule' }
    else if (url.searchParams.get('ws') === 'pulse') url.searchParams.delete('ws')
    window.history.replaceState(null, '', url)
  }, [active])

  useEffect(() => {
    if (!active) return
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = '' }
  }, [active])

  // seat data loads async; wait for it before deciding access on a refresh-restore
  useEffect(() => {
    if (seatReady) return
    const id = setInterval(() => { if (isSeatLoaded()) { setSeatReady(true); clearInterval(id) } }, 100)
    return () => clearInterval(id)
  }, [seatReady])

  useEffect(() => {
    if (active && seatReady && !(hasModule('workload') || hasModule('admin'))) setActive(false)
  }, [active, seatReady])

  // load recent runs when the workspace opens
  useEffect(() => {
    if (!active || !seatReady || !(hasModule('workload') || hasModule('admin'))) return
    loadRuns(); loadDigest()
    return () => { pollTimers.current.forEach(clearTimeout); pollTimers.current = [] }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, seatReady])

  async function loadRuns() {
    setLoadingRuns(true)
    try { setRuns(await getSchedulerRuns()) }
    catch (e) { setMsg({ kind: 'err', text: e.message }) }
    finally { setLoadingRuns(false) }
  }

  async function runSnapshot() {
    if (snapBusy) return
    setSnapBusy(true); setSnapMsg(null)
    try {
      await triggerSnapshot()
      setSnapMsg({ kind: 'ok', text: "Snapshot dispatched — today's load + throughput are recorded in ~1 minute." })
    } catch (e) {
      setSnapMsg({ kind: 'err', text: e.message })
    } finally { setSnapBusy(false) }
  }

  async function loadDigest() {
    setLoadingDigest(true)
    try { setDigest(await getSchedulerRunHistory(8)) }
    catch (e) { /* digest is best-effort — leave prior state */ }
    finally { setLoadingDigest(false) }
  }

  async function sendChat(e) {
    if (e) e.preventDefault()
    const q = chatInput.trim()
    if (!q || chatBusy) return
    const next = [...chat, { role: 'user', content: q }]
    setChat(next); setChatInput(''); setChatBusy(true)
    try {
      const reply = await askSchedule(next)
      setChat([...next, { role: 'assistant', content: reply || '(no answer)' }])
    } catch (err) {
      setChat([...next, { role: 'assistant', content: 'Error: ' + err.message }])
    } finally { setChatBusy(false) }
  }

  async function run() {
    if (busy) return
    if (mode === 'live') {
      const scope = testProject.trim() ? `project "${testProject.trim()}"` : 'ALL active projects (firm-wide)'
      const ok = await confirmDialog({
        title: 'Run scheduler live?',
        message: `A live run will reschedule and write dates for ${scope}, the same as the nightly job. Proceed?`,
        confirmLabel: 'Run live', danger: true,
      })
      if (!ok) return
    }
    setBusy(true); setMsg(null)
    try {
      await triggerSchedulerRun({ mode, testProject: testProject.trim() })
      setMsg({ kind: 'ok', text: `${mode === 'live' ? 'Live' : 'Read-only'} run dispatched. It appears below in a few seconds.` })
      // The dispatched run takes a moment to register; poll a couple of times.
      pollTimers.current.forEach(clearTimeout)
      pollTimers.current = [setTimeout(loadRuns, 3500), setTimeout(loadRuns, 9000)]
    } catch (e) {
      setMsg({ kind: 'err', text: e.message })
    } finally { setBusy(false) }
  }

  if (!active) return null

  const restricted = isSeatLoaded() && !(hasModule('workload') || hasModule('admin'))

  return (
    <div className="aj-pulse-ws" style={S.panel}>
      <WorkspaceHeader title="Scheduler" />

      {restricted ? (
        <div style={S.restricted}>This is a manager tool. Contact your administrator.</div>
      ) : (
        <div style={S.content}><div style={S.inner}>
          {/* ── Run control ────────────────────────────────────────────── */}
          <section style={S.card}>
            <div style={S.cardHead}>Run the scheduler</div>
            <p style={S.cardSub}>
              Fires the same nightly job on demand. <b>Read-only</b> computes and previews changes
              without writing anything; <b>Live</b> reschedules and writes dates (Airtable + the Supabase master).
            </p>
            <div style={S.controls}>
              <div style={S.segWrap}>
                <button style={segStyle(mode === 'read_only')} onClick={() => setMode('read_only')}>Read-only</button>
                <button style={segStyle(mode === 'live')} onClick={() => setMode('live')}>Live</button>
              </div>
              <ProjectPicker value={testProject} onChange={setTestProject} />
              <button style={{ ...S.runBtn, ...(busy ? S.runBtnBusy : {}) }} onClick={run} disabled={busy}>
                {busy ? 'Dispatching…' : 'Run'}
              </button>
            </div>
            {msg && <div style={{ ...S.msg, color: msg.kind === 'err' ? '#c0392b' : '#1D9E75' }}>{msg.text}</div>}
          </section>

          {/* ── Practice snapshot ──────────────────────────────────────── */}
          <section style={S.card}>
            <div style={S.cardHead}>Practice snapshot</div>
            <p style={S.cardSub}>
              Records today's measured load + throughput per person and project — the data behind the
              workload charts. Runs nightly; capture on demand here.
            </p>
            <div style={S.controls}>
              <button style={{ ...S.runBtn, ...(snapBusy ? S.runBtnBusy : {}) }} onClick={runSnapshot} disabled={snapBusy}>
                {snapBusy ? 'Capturing…' : 'Capture today'}
              </button>
              {snapMsg && <span style={{ ...S.msg, marginTop: 0, color: snapMsg.kind === 'err' ? '#c0392b' : '#1D9E75' }}>{snapMsg.text}</span>}
            </div>
          </section>

          {/* ── Recent runs ────────────────────────────────────────────── */}
          <section style={S.card}>
            <div style={S.cardHeadRow}>
              <span style={S.cardHead}>Recent runs</span>
              <button style={S.refreshBtn} onClick={loadRuns} disabled={loadingRuns}>{loadingRuns ? 'Refreshing…' : 'Refresh'}</button>
            </div>
            {runs == null ? (
              <div style={S.muted}>Loading…</div>
            ) : runs.length === 0 ? (
              <div style={S.muted}>No runs found.</div>
            ) : (
              <div style={S.runList}>
                {runs.map(r => (
                  <a key={r.id} href={r.html_url} target="_blank" rel="noreferrer" style={S.runRow}>
                    <span style={{ ...S.dot, background: runColor(r) }} />
                    <span style={S.runLabel}>{runVerdict(r)}</span>
                    <span style={S.runMeta}>{r.event === 'workflow_dispatch' ? 'manual' : r.event}</span>
                    <span style={S.runTime}>{fmtTime(r.created_at)}</span>
                    <span style={S.runOpen}>open ↗</span>
                  </a>
                ))}
              </div>
            )}
          </section>

          {/* ── What changed last night (latest scheduler run) ─────────── */}
          <section style={S.card}>
            <div style={S.cardHeadRow}>
              <span style={S.cardHead}>What changed last night</span>
              <button style={S.refreshBtn} onClick={loadDigest} disabled={loadingDigest}>{loadingDigest ? 'Refreshing…' : 'Refresh'}</button>
            </div>
            {digest == null ? (
              <div style={S.muted}>Loading…</div>
            ) : digest.length === 0 ? (
              <div style={S.muted}>No scheduler runs recorded yet. After the nightly job (or a manual run) finishes, the day's changes — and why — appear here.</div>
            ) : (
              <Digest run={digest[0]} />
            )}
          </section>

          {/* ── Ask about the schedule (read-only chat) ────────────────── */}
          <section style={S.card}>
            <div style={S.cardHead}>Ask about the schedule</div>
            <p style={S.cardSub}>
              Questions about what moved and why — e.g. “who's overloaded?”, “why did the Curtis CDs slip?”,
              “what's most at risk this week?”. Answers from the recent run data; read-only.
            </p>
            {chat.length > 0 && (
              <div style={S.chatLog}>
                {chat.map((m, i) => <div key={i} style={m.role === 'user' ? S.chatUser : S.chatAsst}>{m.content}</div>)}
                {chatBusy && <div style={S.chatAsst}>Thinking…</div>}
              </div>
            )}
            <form style={S.chatForm} onSubmit={sendChat}>
              <input style={S.input} placeholder="Ask a question…" value={chatInput} onChange={e => setChatInput(e.target.value)} />
              <button type="submit" style={{ ...S.runBtn, ...(chatBusy ? S.runBtnBusy : {}) }} disabled={chatBusy}>Ask</button>
            </form>
          </section>
        </div></div>
      )}
    </div>
  )
}

function runVerdict(r) {
  if (r.status !== 'completed') return r.status === 'in_progress' ? 'Running…' : 'Queued…'
  if (r.conclusion === 'success') return 'Success'
  if (r.conclusion === 'failure') return 'Failed'
  return r.conclusion || 'Done'
}
function runColor(r) {
  if (r.status !== 'completed') return '#b8540a'
  if (r.conclusion === 'success') return '#1D9E75'
  if (r.conclusion === 'failure') return '#c0392b'
  return '#7a9aaa'
}
function fmtTime(iso) {
  try { return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) }
  catch { return iso || '' }
}

/* ── "What changed" digest, from a persisted schedule_runs row ────────────────────────────────── */
function deltaDays(a, b) {
  if (!a || !b) return 0
  const da = Date.parse(String(a).slice(0, 10) + 'T00:00:00Z'), db = Date.parse(String(b).slice(0, 10) + 'T00:00:00Z')
  return (isNaN(da) || isNaN(db)) ? 0 : Math.round((db - da) / 86400000)
}
function fmtD(d) {
  if (!d) return '—'
  const ms = Date.parse(String(d).slice(0, 10) + 'T00:00:00Z')
  return isNaN(ms) ? '—' : new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}
function stripMd(s) { return String(s || '').replace(/[*_]/g, '') }

function Score({ n, label, color }) {
  return <div style={S.score}><div style={{ ...S.scoreN, color: color || 'var(--aj-text)' }}>{n ?? 0}</div><div style={S.scoreL}>{label}</div></div>
}

function Digest({ run }) {
  const p = run.payload || {}
  const overdue = p.overdue_reactive || []
  const completed = p.completed_yesterday || []
  const moved = (p.milestones || [])
    .filter(m => m.is_pushed || m.is_pulled)
    .map(m => ({ ...m, delta: deltaDays(m.current_lft, m.proposed_finish) }))
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta))
  return (
    <div>
      <div style={S.digestMeta}>{p.date || run.run_date}{run.mode && run.mode !== 'live' ? ` · ${run.mode} preview` : ''}</div>
      <div style={S.scoreRow}>
        <Score n={run.scheduled} label="scheduled" />
        <Score n={run.pushed} label="pushed" color={run.pushed ? '#b8540a' : undefined} />
        <Score n={run.pulled} label="pulled" color={run.pulled ? '#1D9E75' : undefined} />
        <Score n={run.cascade_updates} label="cascaded" />
        <Score n={overdue.length} label="outside overdue" color={overdue.length ? '#c0392b' : undefined} />
        <Score n={completed.length} label="completed" color={completed.length ? '#1D9E75' : undefined} />
      </div>
      {run.summary && <div style={S.summary}>{stripMd(run.summary)}</div>}
      {moved.length > 0 && (
        <>
          <div style={S.subHead}>Milestones that moved</div>
          <div style={S.movedList}>
            {moved.slice(0, 20).map((m, i) => (
              <div key={i} style={S.movedRow}>
                <span style={{ ...S.deltaBadge, color: m.delta > 0 ? '#b8540a' : '#1D9E75', background: (m.delta > 0 ? '#b8540a' : '#1D9E75') + '18' }}>{m.delta > 0 ? '+' : ''}{m.delta}d</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={S.movedName}>{m.milestone}</div>
                  <div style={S.movedSub}>{[m.project, m.contact].filter(Boolean).join(' · ')} · {fmtD(m.current_lft)} → {fmtD(m.proposed_finish)}{m.push_count ? ` · pushed ${m.push_count}×` : ''}</div>
                </div>
              </div>
            ))}
          </div>
          {moved.length > 20 && <div style={S.muted}>+{moved.length - 20} more</div>}
        </>
      )}
      {overdue.length > 0 && (
        <>
          <div style={S.subHead}>Still waiting on outside parties</div>
          {overdue.map((o, i) => (
            <div key={i} style={S.overdueRow}>{o.milestone} <span style={{ color: 'var(--aj-text-muted)' }}>· {o.project} · {o.days_overdue}d overdue</span></div>
          ))}
        </>
      )}
    </div>
  )
}

function segStyle(on) {
  return {
    padding: '6px 14px', fontFamily: '"DM Sans", sans-serif', fontSize: 12, fontWeight: on ? 600 : 400,
    color: on ? '#fff' : 'var(--aj-text)', background: on ? '#07272D' : 'transparent', border: 'none',
    cursor: 'pointer', borderRadius: 4,
  }
}

const S = {
  panel: { display: 'flex', flexDirection: 'column' },   // position/size/bg from .aj-pulse-ws in outline.css
  // Full-width scroll container (scrollbar at the screen's outer edge); content width-limited within.
  content: { flex: 1, overflow: 'auto' },
  inner: { maxWidth: 920, width: '100%', margin: '0 auto', padding: '20px 24px' },
  restricted: { flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"DM Sans", sans-serif', fontSize: 13, color: 'var(--aj-text-mid)' },

  card: { background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 10, padding: 18, marginBottom: 16 },
  cardHead: { fontFamily: '"Montserrat", sans-serif', fontWeight: 600, fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)' },
  cardHeadRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  cardSub: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, lineHeight: 1.5, color: 'var(--aj-text-mid)', margin: '8px 0 14px' },

  controls: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' },
  segWrap: { display: 'flex', gap: 2, padding: 2, background: 'var(--aj-border)', borderRadius: 6 },
  input: {
    flex: '1 1 260px', minWidth: 200, padding: '7px 10px', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5,
    border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, color: 'var(--aj-text)',
  },
  pickerWrap: { position: 'relative', flex: '1 1 260px', minWidth: 200 },
  input2: {
    width: '100%', boxSizing: 'border-box', padding: '7px 10px', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5,
    border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, color: 'var(--aj-text)',
  },
  pickerMenu: {
    position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: 'var(--aj-bg-elevated)',
    border: '0.5px solid var(--aj-border-mid)', borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.16)',
    maxHeight: 240, overflowY: 'auto', zIndex: 50,
  },
  pickerItem: {
    display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none',
    padding: '7px 10px', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text)', cursor: 'pointer',
    borderBottom: '0.5px solid var(--aj-border)',
  },
  pickerNum: { fontFamily: '"DM Mono", monospace', fontSize: 10.5, color: 'var(--aj-text-muted)', marginRight: 8 },
  pickerEmpty: { padding: '8px 10px', fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#8a9ba3', fontStyle: 'italic' },
  runBtn: { padding: '7px 18px', fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, fontWeight: 600, color: '#fff', background: '#07272D', border: 'none', borderRadius: 4, cursor: 'pointer' },
  runBtnBusy: { opacity: 0.6, cursor: 'default' },
  msg: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, marginTop: 12 },

  refreshBtn: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text)', background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, padding: '3px 10px', cursor: 'pointer' },
  muted: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', marginTop: 12 },
  runList: { marginTop: 12, display: 'flex', flexDirection: 'column' },
  runRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '8px 6px', borderTop: '0.5px solid var(--aj-border)', textDecoration: 'none', color: 'var(--aj-text)' },
  dot: { width: 8, height: 8, borderRadius: 4, flexShrink: 0 },
  runLabel: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, fontWeight: 600, width: 80 },
  runMeta: { fontFamily: '"DM Sans", sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', width: 70 },
  runTime: { fontFamily: '"DM Mono", monospace', fontSize: 11.5, color: 'var(--aj-text-mid)', flex: 1 },
  runOpen: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, color: 'var(--aj-accent-blue)' },

  soon: { background: 'var(--aj-border)', borderStyle: 'dashed' },

  digestMeta: { fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 10 },
  scoreRow: { display: 'flex', flexWrap: 'wrap', gap: 8, margin: '12px 0' },
  score: { flex: '1 1 auto', minWidth: 78, background: 'var(--aj-bg-subtle)', borderRadius: 8, padding: '8px 12px' },
  scoreN: { fontFamily: '"DM Mono",monospace', fontSize: 19, fontWeight: 600, lineHeight: 1.1 },
  scoreL: { fontFamily: '"DM Sans",sans-serif', fontSize: 10, color: 'var(--aj-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 2 },
  summary: { fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.55, color: 'var(--aj-text-mid)', whiteSpace: 'pre-wrap', background: 'var(--aj-bg-subtle)', borderRadius: 8, padding: '12px 14px', margin: '4px 0 6px' },
  subHead: { fontFamily: '"Montserrat",sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text)', margin: '14px 0 6px' },
  movedList: { display: 'flex', flexDirection: 'column' },
  movedRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderTop: '0.5px solid var(--aj-border)' },
  deltaBadge: { fontFamily: '"DM Mono",monospace', fontSize: 10.5, fontWeight: 700, padding: '2px 7px', borderRadius: 4, flexShrink: 0, width: 44, textAlign: 'center' },
  movedName: { fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 600, color: 'var(--aj-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  movedSub: { fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  overdueRow: { fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text)', padding: '4px 0' },

  chatLog: { display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 320, overflowY: 'auto', margin: '4px 0 12px' },
  chatUser: { alignSelf: 'flex-end', maxWidth: '85%', background: '#07272D', color: '#fff', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.5, padding: '8px 12px', borderRadius: '10px 10px 2px 10px', whiteSpace: 'pre-wrap' },
  chatAsst: { alignSelf: 'flex-start', maxWidth: '85%', background: 'var(--aj-bg-subtle)', color: 'var(--aj-text-mid)', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, lineHeight: 1.5, padding: '8px 12px', borderRadius: '10px 10px 10px 2px', whiteSpace: 'pre-wrap' },
  chatForm: { display: 'flex', gap: 8, alignItems: 'center' },
}
